import { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Grid, Stack, Typography, Box, Card, Fab, Badge } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useGetProducts } from 'src/api/product';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Iconify from 'src/components/iconify/iconify';
import { ProductItemSkeleton } from 'src/sections/product/product-skeleton';
import {
  ORDERING_SECTIONS,
  useOrderingActiveSection,
  useProductListingPath,
} from 'src/hooks/use-ordering-flow';
import { useOrderingV2Ui } from 'src/contexts/ordering-v2-ui-context';
import OrderV2CategoryStrip from 'src/sections/ordering-v2/order-v2-category-strip';
import OrderV2ProductSections from 'src/sections/ordering-v2/order-v2-product-sections';
import OrderV2StoriesRow from 'src/sections/ordering-v2/order-v2-stories-row';
import { paths } from 'src/routes/paths';
import { OV2 } from 'src/layouts/ordering-v2/ordering-v2-tokens';
import img from '../assets/bg.png';

// ----------------------------------------------------------------------

const ProductPlaceholderStyle = styled('img')({
  width: '30%',
  height: '30%',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

const ProductImgStyle = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
});

const PRODUCTS_BASE = paths.dashboard.v2.product.root;

// ----------------------------------------------------------------------

export default function ProductsV2() {
  const { path, searchParams, setSearchParams } = useProductListingPath({
    skipDefaultWhenCheckout: false,
  });
  const {
    products: apiProducts,
    categories: apiCategories,
    brands,
    brand_group,
    brand_categories,
    locations,
    locationQuery,
    location,
    productsLoading,
  } = useGetProducts(`${path}`);
  const { totalItems } = useSelector((state) => state.checkout);
  const { setMenuLocation, setCrossSellProducts, openProductDetail, openCart } = useOrderingV2Ui();

  const navigate = useNavigate();
  const products = useMemo(() => {
    const q = searchParams.get('q');
    const num = parseInt(q, 10);
    if (q) {
      return apiProducts.filter(
        (p) => p?.name?.toLowerCase().includes(q) || (num && parseInt(p.price || p.actual_price, 10) === num)
      );
    }
    return apiProducts;
  }, [apiProducts, searchParams]);

  const categories = useMemo(() => {
    const brand_id = searchParams.get('brand_id');

    if (brand_id) {
      const brand_groups = brand_group?.filter((bg) => bg.brand_id === Number(brand_id)).map((bg) => bg.group_id);
      const groups = brand_categories?.filter((bc) => brand_groups.includes(bc.group_id)).map((bc) => bc.categories);
      return apiCategories?.filter((c) => groups?.includes(c.id));
    }

    return apiCategories;
  }, [apiCategories, brand_categories, brand_group, searchParams]);

  const [activeCategoryId, setActiveCategoryId] = useState(() => categories?.[0]?.id);

  useEffect(() => {
    setMenuLocation(location);
  }, [location, setMenuLocation]);

  useEffect(() => {
    setCrossSellProducts(products.slice(0, 12));
  }, [products, setCrossSellProducts]);

  useEffect(() => {
    if (categories?.length && activeCategoryId == null) {
      setActiveCategoryId(categories[0].id);
    }
  }, [categories, activeCategoryId]);

  useEffect(() => {
    if (!categories?.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const raw = en.target.id?.replace(/^cat/, '');
            const num = Number(raw);
            setActiveCategoryId(Number.isNaN(num) ? raw : num);
          }
        });
      },
      { rootMargin: '-12% 0px -55% 0px', threshold: [0, 0.1, 0.25] }
    );

    const t = setTimeout(() => {
      categories.forEach((c) => {
        const el = document.getElementById(`cat${c.id}`);
        if (el) obs.observe(el);
      });
    }, 300);

    return () => {
      clearTimeout(t);
      obs.disconnect();
    };
  }, [categories]);

  const sections = ORDERING_SECTIONS;
  const activeSection = useOrderingActiveSection(locationQuery, brands, searchParams);

  const handleBack = () => {
    if (activeSection === sections.CATEGORIES && searchParams?.get('brand_id')) {
      searchParams.delete('brand_id');
      setSearchParams(searchParams);
    } else {
      navigate(`${PRODUCTS_BASE}?customer_id=${location?.customer_id?.toString()}`);
    }
  };

  const onOpenProduct = useCallback(
    (p) => {
      openProductDetail(p);
    },
    [openProductDetail]
  );

  return productsLoading ? (
    <Grid container spacing={4}>
      {Array.from(Array(12).keys()).map((i) => (
        <Grid key={i} item xs={12} sm={6} md={4} lg={3}>
          <ProductItemSkeleton />
        </Grid>
      ))}
    </Grid>
  ) : (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        maxWidth: { xs: '100%', sm: '100%', md: OV2.contentMaxWidth },
        mx: 'auto',
        px: { xs: 2, sm: 2.5, md: 3.5, lg: 4 },
        pb: 4,
      }}
    >
      {activeSection === sections.BRANDS && searchParams?.get('customer_id') && (
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          onClick={() => {
            navigate(`${PRODUCTS_BASE}?customer_id=${location?.customer_id?.toString()}`);
          }}
        >
          <Iconify icon="mdi:arrow-left" /> Back to all locations
        </Typography>
      )}

      {activeSection === sections.CATEGORIES && searchParams?.get('customer_id') && (
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          onClick={handleBack}
        >
          <Iconify icon="mdi:arrow-left" /> Back to all brands and locations
        </Typography>
      )}

      {activeSection !== sections.CATEGORIES && (
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 800 }}>
          {activeSection === sections.LOCATIONS && `Choose From Trusted Restaurants in One Single Order`}
          {activeSection === sections.BRANDS && `Choose Brand`}
        </Typography>
      )}

      {activeSection === sections.BRANDS && (
        <Grid container spacing={3}>
          {brands?.map((b) => (
            <Grid key={b.id} item xs={6} sm={6} md={3}>
              <Card
                onClick={() => {
                  navigate(
                    `${PRODUCTS_BASE}?customer_id=${b?.customer_id}&location_id=${location?.id}&brand_id=${b?.id}`
                  );
                }}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: (theme) => theme.customShadows.z20,
                  },
                }}
              >
                <Box sx={{ pt: '100%', position: 'relative' }}>
                  {b.brand_thumbnail ? (
                    <ProductImgStyle
                      alt={b.brand_name}
                      src={`https://syspos.s3.me-central-1.amazonaws.com/media/${b.brand_thumbnail}`}
                    />
                  ) : (
                    <ProductPlaceholderStyle alt={b.brand_name} src={img} />
                  )}
                </Box>

                <Stack spacing={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {b.brand_name}
                  </Typography>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {activeSection === sections.LOCATIONS && (
        <Grid container spacing={3}>
          {locations?.map((l) => (
            <Grid key={l.id} item xs={12} sm={6} md={3}>
              <Card
                onClick={() => {
                  navigate(`${PRODUCTS_BASE}?customer_id=${l?.customer_id}&location_id=${l.id?.toString()}`);
                }}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: (theme) => theme.customShadows.z20,
                  },
                }}
              >
                <Box sx={{ pt: '100%', position: 'relative' }}>
                  {l.logo_image ? (
                    <ProductImgStyle
                      alt={l.name}
                      src={`https://syspos.s3.me-central-1.amazonaws.com/media/${l.logo_image}`}
                    />
                  ) : (
                    <ProductPlaceholderStyle alt={l.name} src={img} />
                  )}
                </Box>

                <Stack spacing={2} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {l.name}
                  </Typography>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {activeSection === sections.CATEGORIES && (
        <>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 800 }}>
            Explore Menu of {location?.name || '...'}
          </Typography>
          <OrderV2StoriesRow />
          <OrderV2CategoryStrip
            categories={categories || []}
            activeCategoryId={activeCategoryId}
            onSelectCategory={setActiveCategoryId}
            showMyOrder={location?.enable_menu_checkout === 1}
          />
          <OrderV2ProductSections
            products={products}
            categories={categories || []}
            currency={location?.currency || 'AED'}
            onOpenProduct={onOpenProduct}
          />
          {location?.enable_menu_checkout === 1 && (
            <Fab
              color="primary"
              aria-label="Cart"
              onClick={openCart}
              sx={{
                position: 'fixed',
                right: 24,
                bottom: 24,
                zIndex: (t) => t.zIndex.speedDial - 1,
                bgcolor: '#0e7c62',
                '&:hover': { bgcolor: '#065f4a' },
              }}
            >
              <Badge badgeContent={totalItems} color="error" max={99}>
                <Iconify icon="eva:shopping-cart-fill" width={24} sx={{ color: '#fff' }} />
              </Badge>
            </Fab>
          )}
        </>
      )}
    </Container>
  );
}
